import { useRef, useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import DropdownMenu from "../../../ui/DropdownMenu";
import DatePill from "../../../ui/DatePill";
import Portal from "../../../ui/Portal";
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';

export default function InvoiceHeader({ invoice, updateField, disabled = false, labels = {}, defaultNumber = "INV-0001" }) {
  const [termsOpen, setTermsOpen] = useState(false);
  const termsRef = useRef(null);

  // Default labels
  const defaultLabels = {
    title: "Invoice",
    number: "Invoice Number", 
    issueDate: "Issue",
    dueDate: "Due",
    terms: "Terms",
  };
  
  const finalLabels = { ...defaultLabels, ...labels };

  function setAndClose(value) {
    if (disabled) return;
    updateField("paymentTerms", value);
    setTermsOpen(false);
  }

  return (
    <section className="space-y-4 font-sans">
      {/* ───────── Title Row ───────── */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          {/* Title + Type */}
          <div className="flex items-baseline gap-4">
            <h1 className="text-4xl font-light tracking-tight text-slate-900">
              {finalLabels.title}
            </h1>

            {/* Type Toggle - More integrated */}
            <div className="inline-flex items-center gap-1 px-1 py-0.5 rounded-lg bg-slate-100 border border-slate-200">
              {["services", "goods"].map((type) => {
                const active = invoice.type === type;
                return (
                  <button
                    key={type}
                    onClick={() => !disabled && updateField("type", type)}
                    disabled={disabled}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all duration-150 ${
                      active
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {type === "services" ? "Services" : "Goods"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Invoice Number - More prominent */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">No.</span>
            <input
              type="text"
              value={invoice.invoiceNumber || defaultNumber}
              onChange={(e) =>
                !disabled && updateField("invoiceNumber", e.target.value)
              }
              disabled={disabled}
              className={`px-2 py-1 text-sm font-mono font-medium text-slate-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:outline-none transition-all duration-150 ${
                disabled ? "opacity-50 cursor-not-allowed" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* ───────── Meta Row ───────── */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <DatePill
          label={finalLabels.issueDate}
          value={invoice.issueDate}
          onChange={(v) => !disabled && updateField("issueDate", v)}
          disabled={disabled}
        />

        <span className="text-slate-300">→</span>

        <DatePill
          label={finalLabels.dueDate}
          value={invoice.dueDate}
          onChange={(v) => !disabled && updateField("dueDate", v)}
          disabled={disabled}
        />

        <span className="text-slate-300">•</span>

        {/* P.O. Date - Using DatePill pattern */}
        <DatePill
          label="P.O."
          value={invoice.poDate}
          onChange={(v) => !disabled && updateField("poDate", v)}
          disabled={disabled}
        />

        <span className="text-slate-300">•</span>

        {/* Reference Number (PO / Agreement) */}
        <RefNumberPill
          poNumber={invoice.poNumber}
          agreementNumber={invoice.agreementNumber}
          onChangePo={(v) => !disabled && updateField("poNumber", v)}
          onChangeAgreement={(v) => !disabled && updateField("agreementNumber", v)}
          disabled={disabled}
        />

        <span className="text-slate-300">•</span>

        {/* Terms Dropdown - Structured Layout */}
        <div ref={termsRef}>
          <button
            type="button"
            onClick={() => !disabled && setTermsOpen(true)}
            disabled={disabled}
            className={`inline-flex flex-col items-start gap-0.5 px-3 py-1.5 rounded-lg transition-all duration-150 hover:shadow-sm ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            style={{
              background: "var(--color-bg-muted)",
              border: "1px solid var(--color-border)",
            }}
          >
            <span className="text-[10px] font-medium text-[var(--color-text-muted)]">
              {finalLabels.terms}
            </span>
            <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {formatTerms(invoice.paymentTerms)}
            </span>
          </button>

          {termsOpen && (
            <DropdownMenu
              anchorRect={termsRef.current?.getBoundingClientRect()}
              onClose={() => setTermsOpen(false)}
            >
              <TermsOption
                value="due-on-receipt"
                current={invoice.paymentTerms}
                onSelect={setAndClose}
              >
                Due on receipt
              </TermsOption>

              <TermsOption
                value="net-15"
                current={invoice.paymentTerms}
                onSelect={setAndClose}
              >
                Net 15
              </TermsOption>

              <TermsOption
                value="net-30"
                current={invoice.paymentTerms}
                onSelect={setAndClose}
              >
                Net 30
              </TermsOption>

              <TermsOption
                value="net-60"
                current={invoice.paymentTerms}
                onSelect={setAndClose}
              >
                Net 60
              </TermsOption>
            </DropdownMenu>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───────── Helpers ───────── */

function formatTerms(value) {
  if (!value || value === "due-on-receipt") return "Due on receipt";
  if (value === "net-15") return "Net 15";
  if (value === "net-30") return "Net 30";
  if (value === "net-60") return "Net 60";
  return value;
}

function TermsOption({ value, current, onSelect, children }) {
  const active = value === current;

  return (
    <button
      onClick={() => onSelect(value)}
      className="w-full text-left px-3 py-2 rounded-md transition-all duration-150 hover:bg-[var(--color-bg-muted)]"
      style={{
        background: active ? "var(--color-bg-muted)" : "transparent",
        color: "var(--color-text-primary)",
        fontWeight: active ? 500 : 400,
      }}
    >
      {children}
    </button>
  );
}

/* ───────── Reference Number Pill (PO / Agreement — user picks which) ───────── */

const REF_TYPES = [
  { key: "po",        label: "PO No.",    placeholder: "PO Number"    },
  { key: "agreement", label: "Agreement", placeholder: "Agreement #"  },
];

function RefNumberPill({ poNumber, agreementNumber, onChangePo, onChangeAgreement, disabled = false }) {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [editing, setEditing]       = useState(null); // 'po' | 'agreement' | null
  const [tempValue, setTempValue]   = useState("");
  const inputRef                    = useRef(null);
  const buttonRef                   = useRef(null);

  // Floating UI for dropdown positioning
  const { refs, floatingStyles } = useFloating({
    open: menuOpen,
    onOpenChange: setMenuOpen,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  });

  const hasAny = poNumber || agreementNumber;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    
    const handler = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        refs.floating.current && !refs.floating.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen, refs.floating]);

  // Close on Escape key
  useEffect(() => {
    if (!menuOpen) return;
    
    const handler = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        buttonRef.current?.focus();
      }
    };
    
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [menuOpen]);

  const startEdit = (type) => {
    setMenuOpen(false);
    setEditing(type);
    setTempValue(type === "po" ? (poNumber || "") : (agreementNumber || ""));
  };

  const handleSave = () => {
    if (editing === "po")        onChangePo(tempValue.trim() || null);
    if (editing === "agreement") onChangeAgreement(tempValue.trim() || null);
    setEditing(null);
  };

  const handleCancel = () => setEditing(null);

  const handleKeyDown = (e) => {
    if (e.key === "Enter")  { e.preventDefault(); handleSave(); }
    if (e.key === "Escape") { e.preventDefault(); handleCancel(); }
  };

  // While editing — show inline input
  if (editing) {
    const meta = REF_TYPES.find(t => t.key === editing);
    return (
      <div className="inline-flex items-center gap-1">
        <span className="text-xs text-[var(--color-text-muted)] px-1">{meta.label}</span>
        <input
          ref={inputRef}
          type="text"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="px-3 py-1.5 text-sm rounded-full border focus:outline-none focus:ring-2 min-w-[140px]"
          style={{ borderColor: "var(--color-primary)", background: "var(--color-bg-canvas)" }}
          placeholder={meta.placeholder}
        />
      </div>
    );
  }

  return (
    <>
      <div className="inline-flex items-center gap-1">
        {/* Show existing values as separate pills if set */}
        {poNumber && (
          <button
            type="button"
            onClick={() => !disabled && startEdit("po")}
            disabled={disabled}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ background: "var(--color-bg-muted)", border: "1px solid var(--color-border)", color: "var(--color-text-primary)" }}
          >
            <span className="text-xs text-[var(--color-text-muted)]">PO No.</span>
            <span className="font-medium">{poNumber}</span>
          </button>
        )}

        {agreementNumber && (
          <button
            type="button"
            onClick={() => !disabled && startEdit("agreement")}
            disabled={disabled}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ background: "var(--color-bg-muted)", border: "1px solid var(--color-border)", color: "var(--color-text-primary)" }}
          >
            <span className="text-xs text-[var(--color-text-muted)]">Agreement</span>
            <span className="font-medium">{agreementNumber}</span>
          </button>
        )}

        {/* Add button — shows when nothing set, or as a + to add the other */}
        {(!hasAny || (!poNumber || !agreementNumber)) && (
          <button
            ref={(node) => {
              buttonRef.current = node;
              refs.setReference(node);
            }}
            type="button"
            onClick={() => !disabled && setMenuOpen(o => !o)}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-muted)",
            }}
          >
            <span className="font-medium">{hasAny ? "+ Add" : "Ref No."}</span>
            <ChevronDown size={14} className="opacity-60" />
          </button>
        )}
      </div>

      {/* Dropdown to pick type - Rendered in Portal */}
      {menuOpen && (
        <Portal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 1000,
            }}
            className="rounded-xl shadow-2xl border py-1 min-w-[160px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ background: "var(--color-bg-canvas)", borderColor: "var(--color-border)" }} className="rounded-xl">
              {REF_TYPES.filter(t => !(t.key === "po" && poNumber) && !(t.key === "agreement" && agreementNumber)).map(t => (
                <button
                  key={t.key}
                  onClick={() => startEdit(t.key)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-bg-muted)] transition first:rounded-t-xl last:rounded-b-xl"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
